import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Trash2 } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import type { Program } from '@/lib/types';
import { fmtDate, fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardHeader, Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';

interface Deleted {
  kind: 'document' | 'task' | 'raid' | 'meeting' | 'vendor';
  id: string;
  name: string;
  detail: string;
  deletedAt: string;
  deletedBy: string;
  restoreUntil: string;
}

/** What BlackWolf deleted in the last 30 days, with Restore. */
export function RecentlyDeleted({ program }: { program: Program }) {
  const qc = useQueryClient();
  const toast = useToast();
  const base = `/api/programs/${program.id}/admin/deleted`;
  const q = useQuery({ queryKey: ['admin', program.id, 'deleted'], queryFn: () => api.get<Deleted[]>(base) });
  const restore = useMutation({
    mutationFn: (x: Deleted) => api.post(`${base}/${x.kind}/${x.id}/restore`),
    onSuccess: (_r, x) => {
      toast.ok(`${x.name} restored.`);
      void qc.invalidateQueries();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card>
      <CardHeader
        title="Recently deleted"
        icon={<Trash2 />}
        tone="neutral"
        description="Documents, tasks, RAID items, meetings and vendors deleted in the last 30 days. Restore puts them back where they were."
      />
      {q.isLoading ? (
        <Spinner className="h-32" />
      ) : !q.data?.length ? (
        <p className="px-5 py-4 text-sm text-muted">Nothing deleted in the last 30 days.</p>
      ) : (
        <ul>
          {q.data.map((x) => (
            <li
              key={`${x.kind}${x.id}`}
              className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-2.5 text-[13px] last:border-0"
            >
              <Badge>{x.detail}</Badge>
              <span className="min-w-0 flex-1 truncate font-medium">{x.name}</span>
              <span className="text-xs text-muted">
                Deleted {fmtDateTime(x.deletedAt)} by {x.deletedBy} · restore until{' '}
                {fmtDate(x.restoreUntil.slice(0, 10))}
              </span>
              <Button
                size="sm"
                variant="secondary"
                loading={restore.isPending && restore.variables?.id === x.id}
                onClick={() => restore.mutate(x)}
              >
                <RotateCcw /> Restore
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
